import { useMutation, useQuery } from '@redwoodjs/web';
import { notify } from 'src/lib/notify';
import { ThirdAndFirstPartyEntities } from 'types/graphql';

const GET_THIRD_PARTY_INTEGRATIONS = gql`
  query ThirdPartyIntegrationsQuery {
    thirdPartyIntegrations(take: 500) {
      id
      type
    }
  }`;

const BEGIN_QUICKBOOKS_CONNECTION = gql`
  query BeginQuickbooksConnection {
    beginQuickbooksConnection {
      authorizationUri
    }
  }`;

const VALIDATE_QUICKBOOKS_CONNECTION = gql`
    query ValidateQuickbooksConnection {
      validateQuickbooksConnection {
        isValid
        integration {
          id
          organizationId
          type
          name
          createdAt
          updatedAt
          deletedAt
        }
      }
    }
  `;

const LINK_QUICKBOOKS_ACCOUNT = gql`
  mutation LinkQuickbooksAccount($thirdPartyId: String!, $ledgerAccountId: Int!) {
    linkQuickbooksAccount(thirdPartyId: $thirdPartyId, ledgerAccountId: $ledgerAccountId) {
      id
    }
  }`;

const COMPLETE_QUICKBOOKS_CONNECTION = gql`
  mutation CompleteQuickbooksConnection($redirectQueryString: String!) {
    completeQuickbooksConnection(input: { redirectQueryString: $redirectQueryString }) {
      connectionSucceeded
      accounts {
        id
      }
    }
  }`;

const DISCONNECT_QUICKBOOKS_CONNECTION = gql`
  mutation DisconnectQuickbooksConnection {
    disconnectQuickbooksConnection
  }`;

const GET_FIRST_AND_THIRD_PARTY_ENTITIES = gql`
  query ThirdAndFirstPartyEntities($filter: ThirdPartyEntityLinkFilter!) {
    thirdPartyIntegrationEntityLinks(filter: $filter) {
      nextCursor
      entities {
        id
        thirdPartyId
        metadata
        account {
          id
          accountName
          accountNumber
          fragmentAccountPath
        }
      }
    }
    ledgerAccountDetails {
      id
      fragmentAccountPath
      accountName
      accountNumber
      accountType
    }
  }`;

export const useGetQuickbooksIntegrationId = () => {
  const { data } = useQuery(GET_THIRD_PARTY_INTEGRATIONS);

  return data?.thirdPartyIntegrations.find((integration) => integration.type === 'QUICKBOOKS_ONLINE')?.id;
};

export const useBeginQuickbooksConnection = () => useQuery(BEGIN_QUICKBOOKS_CONNECTION);

export const useValidateQuickbooksConnection = () => useQuery(VALIDATE_QUICKBOOKS_CONNECTION);

export const useCompleteQuickbooksConnection = () =>
  useMutation(COMPLETE_QUICKBOOKS_CONNECTION, {
    refetchQueries: [GET_THIRD_PARTY_INTEGRATIONS, GET_FIRST_AND_THIRD_PARTY_ENTITIES],
    onCompleted: () => {
      notify.success('Quickbooks connected!');
    },
  });

export const useDisconnectQuickbooksConnection = () =>
  useMutation(DISCONNECT_QUICKBOOKS_CONNECTION, {
    refetchQueries: [GET_THIRD_PARTY_INTEGRATIONS, VALIDATE_QUICKBOOKS_CONNECTION],
    awaitRefetchQueries: true,
    onCompleted: () => {
      notify.success('Quickbooks disconnected');
    },
  });

export const useLinkQuickbooksAccount = () =>
  useMutation(LINK_QUICKBOOKS_ACCOUNT, {
    refetchQueries: [GET_FIRST_AND_THIRD_PARTY_ENTITIES],
    awaitRefetchQueries: true,
    onCompleted: () => {
      notify.success('Accounts linked!');
    },
  });

export const useThirdAndFirstPartyEntities = () =>
  useQuery<ThirdAndFirstPartyEntities>(GET_FIRST_AND_THIRD_PARTY_ENTITIES, {
    variables: { filter: { entityTypes: ['ACCOUNT'], integrationTypes: ['QUICKBOOKS_ONLINE'] } },
  });

const GET_QUICKBOOKS_PREFERENCES = gql`
  query QuickbooksPreferences {
    quickbooksPreferences {
      referenceFormat
      syncDraftBills
    }
  }`;

const UPDATE_QUICKBOOKS_PREFERENCES = gql`
  mutation UpdateQuickbooksPreferences($input: UpdateQuickbooksPreferencesInput!) {
    updateQuickbooksPreferences(input: $input) {
      referenceFormat
      syncDraftBills
    }
  }`;

export const useQuickbooksPreferences = () => useQuery(GET_QUICKBOOKS_PREFERENCES);

export const useUpdateQuickbooksPreferences = () =>
  useMutation(UPDATE_QUICKBOOKS_PREFERENCES, { refetchQueries: [GET_QUICKBOOKS_PREFERENCES] });
